import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW } from '../shared/theme'
import { fadeIn, entrance, pulse, breathe, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * TextReveal — Texto cinematico animado.
 * Estilos: typewriter, fade, split, cinematic
 *
 * params: { text, style }
 */
export default function TextReveal({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { text = '', style = 'cinematic', accent_color } = params
  const texture = artDirection?.texture

  if (style === 'typewriter')
    return (
      <TypewriterReveal
        frame={frame}
        fps={fps}
        text={text}
        texture={texture}
        accentColor={accent_color}
      />
    )
  if (style === 'split')
    return (
      <SplitReveal
        frame={frame}
        fps={fps}
        text={text}
        texture={texture}
        accentColor={accent_color}
      />
    )
  if (style === 'fade')
    return (
      <FadeReveal
        frame={frame}
        fps={fps}
        text={text}
        texture={texture}
        accentColor={accent_color}
      />
    )
  return (
    <CinematicReveal
      frame={frame}
      fps={fps}
      text={text}
      texture={texture}
      accentColor={accent_color}
    />
  )
}

function CinematicReveal({ frame, fps, text, texture, accentColor }) {
  const color = accentColor || COLORS.accent.red
  const lineProgress = entrance(frame, fps, 0, SPRINGS.slow)
  const lineWidth = interpolate(lineProgress, [0, 1], [0, 240])
  const textOpacity = interpolate(frame, [15, 35], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const textY = interpolate(frame, [15, 35], [20, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const glowPulse = pulse(frame, 0.04, 0.5, 1.0)
  const textBreath = breathe(frame, 0.025, 0.008)

  return (
    <SceneWrapper accent={color} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
        }}
      >
        {/* Linea decorativa superior */}
        <div
          style={{
            width: lineWidth,
            height: 2,
            background: color,
            borderRadius: 1,
            boxShadow: GLOW.md(color),
            opacity: 0.6 + glowPulse * 0.4,
          }}
        />
        {/* Texto principal */}
        <div
          style={{
            opacity: textOpacity,
            transform: `translateY(${textY}px) scale(${textBreath})`,
            fontFamily: FONTS.heading,
            fontSize: text.length > 40 ? 64 : 80,
            fontWeight: 700,
            color: COLORS.text.primary,
            textAlign: 'center',
            maxWidth: 1400,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            padding: '0 80px',
          }}
        >
          {text}
        </div>
        {/* Linea decorativa inferior */}
        <div
          style={{
            width: lineWidth,
            height: 2,
            background: color,
            borderRadius: 1,
            boxShadow: GLOW.md(color),
            opacity: 0.6 + glowPulse * 0.4,
          }}
        />
      </div>
    </SceneWrapper>
  )
}

function FadeReveal({ frame, fps, text, texture, accentColor }) {
  const color = accentColor || COLORS.accent.purple
  const opacity = fadeIn(frame, 0, 30)
  const scale = interpolate(frame, [0, 30], [0.95, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  return (
    <SceneWrapper accent={color} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity,
          transform: `scale(${scale})`,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.heading,
            fontSize: 72,
            fontWeight: 700,
            color: COLORS.text.primary,
            textAlign: 'center',
            maxWidth: 1400,
            lineHeight: 1.2,
            padding: '0 80px',
          }}
        >
          {text}
        </div>
      </div>
    </SceneWrapper>
  )
}

function SplitReveal({ frame, fps, text, texture, accentColor }) {
  const color = accentColor || COLORS.accent.red
  const words = text.split(' ')
  const mid = Math.ceil(words.length / 2)
  const topHalf = words.slice(0, mid).join(' ')
  const bottomHalf = words.slice(mid).join(' ')

  const topY = interpolate(frame, [0, 20], [-60, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const botY = interpolate(frame, [0, 20], [60, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const opacity = fadeIn(frame, 0, 15)

  return (
    <SceneWrapper accent={color} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          opacity,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.heading,
            fontSize: 80,
            fontWeight: 800,
            color: COLORS.text.primary,
            textAlign: 'center',
            transform: `translateY(${topY}px)`,
            lineHeight: 1.1,
          }}
        >
          {topHalf}
        </div>
        {bottomHalf && (
          <div
            style={{
              fontFamily: FONTS.heading,
              fontSize: 80,
              fontWeight: 800,
              color,
              textAlign: 'center',
              transform: `translateY(${botY}px)`,
              lineHeight: 1.1,
              textShadow: GLOW.text(color),
            }}
          >
            {bottomHalf}
          </div>
        )}
      </div>
    </SceneWrapper>
  )
}

function TypewriterReveal({ frame, fps, text, texture, accentColor }) {
  const color = accentColor || COLORS.accent.blue
  const charsToShow = Math.floor(
    interpolate(frame, [0, fps * 2], [0, text.length], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })
  )
  const showCursor = frame % 20 < 10
  const cursorPulse = pulse(frame, 0.1, 0.4, 1.0)

  return (
    <SceneWrapper accent={color} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 60,
            fontWeight: 500,
            color: COLORS.text.primary,
            textAlign: 'center',
            maxWidth: 1400,
            padding: '0 80px',
            lineHeight: 1.4,
          }}
        >
          {text.slice(0, charsToShow)}
          {showCursor && (
            <span
              style={{
                color,
                marginLeft: 4,
                opacity: cursorPulse,
                textShadow: GLOW.text(color),
              }}
            >
              |
            </span>
          )}
        </div>
      </div>
    </SceneWrapper>
  )
}
